import { MutableRefObject } from 'react';
import { Network, DataSet } from 'vis-network/standalone';
import { Node, Edge, NodeLabelSettings, EdgeLabelSettings } from '../types';
import { getAllNodesWithLabels, getAllEdgesWithLabels } from '../lib/styleProcessor';
import { viewportStateManager } from '../viewportStateManager';
import { AnimatingNode } from './useExpansionSlideAnimation';
import i18n from 'i18next';

export interface UseGraphExpansionProps {
  setExpandedNodes: React.Dispatch<React.SetStateAction<Set<string>>>;
  setIsExpansionLoading: (loading: boolean) => void;
  setExpansionMessage: (message: string) => void;
  graphNodes: DataSet<Node>;
  graphEdges: DataSet<Edge>;
  isExpanding: MutableRefObject<boolean>;
  networkRef: MutableRefObject<Network | null>;
  graphMetaData: any;
  graphVizBehaviourOptions: any;
  physicsSettings: any;
  setGraphDataVersion: React.Dispatch<React.SetStateAction<number>>;
  unlockAllNodes: () => void;
  setNewlyAddedNodes: React.Dispatch<React.SetStateAction<Set<string>>>;
  setNewlyAddedEdges: React.Dispatch<React.SetStateAction<Set<string>>>;
  setShowNewAdditions: (show: boolean) => void;
  getAccessToken: () => string | undefined;
  orgNumber: string;
  highlightedNodes: Set<string>;
  expandedNodes: Set<string>;
  nodeLabelSettings: NodeLabelSettings;
  edgeLabelSettings: EdgeLabelSettings;
  setStabilizationComplete?: (complete: boolean) => void;
  onExpansionComplete?: (newNodes: Node[], newEdges: Edge[]) => void;
  onSlideAnimation?: (nodes: AnimatingNode[]) => void;
  isDarkMode?: boolean;
  showCompanyComments?: boolean;
}

export const useGraphExpansion = ({
  setExpandedNodes,
  setIsExpansionLoading,
  setExpansionMessage,
  graphNodes,
  graphEdges,
  isExpanding,
  networkRef,
  graphMetaData,
  graphVizBehaviourOptions,
  physicsSettings,
  setGraphDataVersion,
  unlockAllNodes,
  setNewlyAddedNodes,
  setNewlyAddedEdges,
  setShowNewAdditions,
  getAccessToken,
  orgNumber,
  highlightedNodes,
  expandedNodes,
  nodeLabelSettings,
  edgeLabelSettings,
  setStabilizationComplete,
  onExpansionComplete,
  onSlideAnimation,
  isDarkMode = false,
  showCompanyComments = true
}: UseGraphExpansionProps) => {

  // Method to handle node expansion by adding new nodes and relationships
  const handleExpandNode = (newNodes: Node[], newRelationships: Edge[], expansionNodeId: string) => {
    
    // Mark the expanded node to remove its overlay icon and hide aggregation info
    setExpandedNodes(prev => new Set(prev).add(expansionNodeId));
    
    // Start loading state
    setIsExpansionLoading(true);
    setExpansionMessage(i18n.t('graph.expansion.analyzing'));

    try {
      // ============================================================================
      // EARLY EXIT: Check if there are any genuinely new nodes to add
      // ============================================================================
      
      // Get current node and edge IDs to check what's actually new
      const currentNodeIds = new Set(graphNodes.getIds().map(id => id.toString()));
      const currentEdgeIds = new Set(graphEdges.getIds().map(id => id.toString()));
      
      // Count how many nodes are genuinely new
      const genuinelyNewNodeCount = newNodes.filter(node => !currentNodeIds.has(String(node.id))).length;
      
      if (genuinelyNewNodeCount === 0) {
        console.log('No new nodes to add. Checking for property updates on existing nodes...');
        
        // Process updates for existing nodes immediately without stabilization
        const updatesForExistingNodes: any[] = [];
        
        newNodes.forEach((newNode: any) => {
          const nodeIdStr = newNode.id?.toString();
          const exists = currentNodeIds.has(nodeIdStr);

          if (exists) {
            let currentNode = graphNodes.get(newNode.id) as any;
            if (!currentNode && nodeIdStr) {
              currentNode = graphNodes.get(nodeIdStr) as any;
            }

            if (currentNode) {
              // Merge properties
              const mergedProperties = {
                  ...(currentNode.properties || {}),
                  ...(newNode.properties || {})
              };
              
              // Ensure specific fields are carried over
              if (newNode.properties && newNode.properties.funktionar_namn) {
                  mergedProperties.funktionar_namn = newNode.properties.funktionar_namn;
              }

              const mergedNode = {
                ...currentNode,
                ...newNode,
                properties: mergedProperties,
                x: currentNode.x,
                y: currentNode.y
              };
              updatesForExistingNodes.push(mergedNode);
            }
          }
        });

        // Style the updates
        const styledUpdates = getAllNodesWithLabels(
          updatesForExistingNodes,
          orgNumber,
          graphMetaData,
          highlightedNodes,
          new Set(),
          false,
          expandedNodes,
          nodeLabelSettings,
          isDarkMode,
          showCompanyComments,
        );
        
        // Style edges
        const processedAllEdges = getAllEdgesWithLabels(
          [...newRelationships], 
          graphVizBehaviourOptions, 
          getAccessToken(), 
          updatesForExistingNodes, 
          new Set(), 
          false, 
          edgeLabelSettings
        );

        // Create update payloads
        const existingNodesToUpdate: any[] = [];
        styledUpdates.forEach((node: any) => {
          const nodeIdStr = node.id?.toString();
          const currentNode = graphNodes.get(node.id) as any || (nodeIdStr ? graphNodes.get(nodeIdStr) as any : null);
          
          if (currentNode) {
            const updatedNode = {
              ...currentNode,
              ...node,
              id: currentNode.id,
              x: currentNode.x,
              y: currentNode.y, // Maintain position
              properties: node.properties,
              metadata: {
                ...currentNode.metadata,
                ...(node as any).metadata,
                cumulativeRelationshipTypes: (node as any).metadata?.cumulativeRelationshipTypes,
                isOnBadPath: (node as any).metadata?.isOnBadPath
              }
            };
            existingNodesToUpdate.push(updatedNode);
          }
        });

        const existingEdgesToUpdate: any[] = [];
        const newEdgesToAdd: any[] = [];
        
        processedAllEdges.forEach((edge: any) => {
          const edgeIdStr = edge.id?.toString();
          
          if (edgeIdStr && currentEdgeIds.has(edgeIdStr)) {
            const currentEdge = graphEdges.get(edge.id) as any || graphEdges.get(edgeIdStr) as any;
            if (currentEdge) {
              const updatedEdge = {
                  ...currentEdge,
                  metadata: {
                  ...currentEdge.metadata,
                  cumulativeRelationshipTypes: edge.metadata?.cumulativeRelationshipTypes || currentEdge.metadata?.cumulativeRelationshipTypes,
                  isOnBadPath: edge.metadata?.isOnBadPath !== undefined ? edge.metadata.isOnBadPath : currentEdge.metadata?.isOnBadPath
                  }
              };
              existingEdgesToUpdate.push(updatedEdge);
            }
          } else {
            newEdgesToAdd.push(edge);
          }
        });

        if (existingNodesToUpdate.length === 0 && newEdgesToAdd.length === 0 && existingEdgesToUpdate.length === 0) {
          setExpansionMessage(i18n.t('graph.expansion.noUpdates'));
          setIsExpansionLoading(false);
          setTimeout(() => setExpansionMessage(''), 3000);
          return; 
        }

        setExpansionMessage(i18n.t('graph.expansion.updating', { nodes: existingNodesToUpdate.length, edges: newEdgesToAdd.length }));

        // Batch Apply updates (A1)
        if (existingNodesToUpdate.length > 0) graphNodes.update(existingNodesToUpdate);
        if (existingEdgesToUpdate.length > 0) graphEdges.update(existingEdgesToUpdate);
        if (newEdgesToAdd.length > 0) graphEdges.add(newEdgesToAdd);

        if (onExpansionComplete) {
          onExpansionComplete([], newEdgesToAdd);
        }
        
        setGraphDataVersion(prev => prev + 1);
        
        setIsExpansionLoading(false);
        setTimeout(() => setExpansionMessage(''), 3000);
        return;
      }
      
      // ============================================================================
      // OPTIMIZED NODE EXPANSION - Step 2
      // ============================================================================
    try {
      console.log(`Starting optimized node expansion: ${genuinelyNewNodeCount} new nodes.`);
      setExpansionMessage(i18n.t('graph.expansion.adding', { count: genuinelyNewNodeCount }));
      isExpanding.current = true;

      if (networkRef.current) {
        networkRef.current.storePositions(); // Ensure positions are fresh
      }

      // Get position of the expansion node
      let expansionPosition = { x: 0, y: 0 };
      if (networkRef.current) {
        const positions = networkRef.current.getPositions([expansionNodeId]);
        if (positions[expansionNodeId]) {
          expansionPosition = positions[expansionNodeId];
        }
      }

      // 1. FREEZE LOGIC
      // E1: Keep existing structure stable, only unfreeze neighbors
      const existingNodeIds = graphNodes.getIds();
      let nodesToKeepUnfrozen = new Set<string>();
      const expansionNodeIdStr = String(expansionNodeId);
      
      // Use graphEdges to find neighbors
      try {
        const allEdges = graphEdges.get(); 
        allEdges.forEach((edge: any) => {
          const from = String(edge.from);
          const to = String(edge.to);
          if (from === expansionNodeIdStr && to !== expansionNodeIdStr) nodesToKeepUnfrozen.add(to);
          else if (to === expansionNodeIdStr && from !== expansionNodeIdStr) nodesToKeepUnfrozen.add(from);
        });
        // Also unfreeze expansion node to allow it to center itself among new neighbors
        nodesToKeepUnfrozen.add(expansionNodeIdStr);
      } catch (e) {
         console.warn("Could not determine neighbors", e);
      }

      // ── Capture pre-expansion positions of unfrozen nodes ──────────────
      // These are the nodes the user can currently see, which are about to be
      // pushed around by stabilization.  We snapshot their positions so we can
      // later show "ghost trail" animations from old → new position.
      const preExpansionPositions: Record<string, { x: number; y: number }> = {};
      if (networkRef.current) {
        const allPosSnap = networkRef.current.getPositions();
        nodesToKeepUnfrozen.forEach(nid => {
          if (allPosSnap[nid]) {
            preExpansionPositions[nid] = { x: allPosSnap[nid].x, y: allPosSnap[nid].y };
          }
        });
      }

      // Apply Freeze
      graphNodes.update(existingNodeIds.map(id => {
          const strId = String(id);
          const shouldKeepUnfrozen = nodesToKeepUnfrozen.has(strId);
          // Fixed nodes can be dragged by user but won't move by physics automatically
          return { id: strId, fixed: !shouldKeepUnfrozen };
      }));

      // 2. PLACEMENT LOGIC
      const genuinelyNewNodes = newNodes.filter(node => !currentNodeIds.has(String(node.id)));
      
      // Grouping Logic (Keep existing grouping logic as it was sound)
      const connectedGroups: Set<string>[] = [];
      const nodeToGroupIndex: Record<string, number> = {};
      
      genuinelyNewNodes.forEach(n => {
        connectedGroups.push(new Set([String(n.id)]));
        nodeToGroupIndex[String(n.id)] = connectedGroups.length - 1;
      });

      newRelationships.forEach(edge => {
        const fromId = String(edge.from);
        const toId = String(edge.to);
        if (nodeToGroupIndex[fromId] !== undefined && nodeToGroupIndex[toId] !== undefined) {
          const groupAIdx = nodeToGroupIndex[fromId];
          const groupBIdx = nodeToGroupIndex[toId];
          if (groupAIdx !== groupBIdx) {
            const groupB = connectedGroups[groupBIdx];
            groupB.forEach(id => {
              connectedGroups[groupAIdx].add(id);
              nodeToGroupIndex[id] = groupAIdx;
            });
            groupB.clear();
          }
        }
      });

      const sortedNodeIds: string[] = [];
      connectedGroups.forEach(group => {
        if (group.size > 0) sortedNodeIds.push(...Array.from(group));
      });

      const sortedNewNodes = sortedNodeIds
        .map(id => genuinelyNewNodes.find(n => String(n.id) === id))
        .filter((n): n is typeof genuinelyNewNodes[0] => !!n);

      if (sortedNewNodes.length !== genuinelyNewNodes.length) {
         genuinelyNewNodes.forEach(n => {
            if (!sortedNewNodes.includes(n)) sortedNewNodes.push(n);
         });
      }

      // Hierarchy & Fan Calculation
      const nodePositions: Record<string, {x: number, y: number}> = {};
      const allPositions = networkRef.current?.getPositions() || {};
      const existingNodeSet = new Set(existingNodeIds.map(String));

      // Re-use hierarchy builder
      const buildHierarchy = (groupIds: string[], anchors: string[]) => {
         const depthMap: Record<string, number> = {};
         const parentMap: Record<string, string> = {};
         const queue: {id: string, depth: number}[] = [];

         anchors.forEach(a => {
            newRelationships.forEach(edge => {
               const from = String(edge.from);
               const to = String(edge.to);
               const other = from === a ? to : (to === a ? from : null);
               if (other && groupIds.includes(other)) {
                   if (depthMap[other] === undefined) {
                     depthMap[other] = 0;
                     parentMap[other] = a;
                     queue.push({ id: other, depth: 0 });
                  }
               }
            });
         });

         let head = 0;
         while(head < queue.length) {
            const current = queue[head++];
            newRelationships.forEach(edge => {
               const from = String(edge.from);
               const to = String(edge.to);
               const neighbor = from === current.id ? to : (to === current.id ? from : null);
               if (neighbor && groupIds.includes(neighbor) && depthMap[neighbor] === undefined) {
                  depthMap[neighbor] = current.depth + 1;
                  parentMap[neighbor] = current.id;
                  queue.push({ id: neighbor, depth: current.depth + 1 });
               }
            });
         }
         return { depthMap, parentMap };
      };

      // Calculate Fan Vector
      let fanBaseAngle = 0;
      let fanSector = 2 * Math.PI; 
      let sourcePos = allPositions[String(orgNumber)];
      const expandPos = allPositions[String(expansionNodeId)];

      if (allPositions && String(expansionNodeId) !== String(orgNumber)) {
        try {
           if (!sourcePos && expandPos) {
               // Find any neighbor as reference
              const firstCurrentNeighbor = Array.from(existingNodeSet).find(nid => {
                  return graphEdges.get().some((e: any) => 
                     (String(e.from) === String(expansionNodeId) && String(e.to) === nid) ||
                     (String(e.to) === String(expansionNodeId) && String(e.from) === nid)
                  );
              });
              if (firstCurrentNeighbor) sourcePos = allPositions[firstCurrentNeighbor];
           }
           if (sourcePos && expandPos) {
              const dx = expandPos.x - sourcePos.x;
              const dy = expandPos.y - sourcePos.y;
              fanBaseAngle = Math.atan2(dy, dx);
              fanSector = (2 * Math.PI) / 3; 
           }
        } catch (e) { /* ignore */ }
      }

      // Group Identification
      const getExistingNeighbors = (nodeId: string) => {
         const neighbors = new Set<string>();
         newRelationships.forEach(edge => {
            const from = String(edge.from);
            const to = String(edge.to);
            if (from === nodeId || to === nodeId) {
               const other = from === nodeId ? to : from;
               if (existingNodeSet.has(other) && other !== String(expansionNodeId)) {
                  neighbors.add(other);
               }
            }
         });
         return Array.from(neighbors);
      };

      const anchoredGroups: { group: string[], anchors: string[] }[] = [];
      const floatingGroups: string[][] = [];

      connectedGroups.forEach(groupSet => {
         if (groupSet.size === 0) return;
         const groupIds = Array.from(groupSet);
         const allAnchors = new Set<string>();
         groupIds.forEach(nodeId => {
            getExistingNeighbors(nodeId).forEach(anchor => allAnchors.add(anchor));
         });
         if (allAnchors.size > 0) anchoredGroups.push({ group: groupIds, anchors: Array.from(allAnchors) });
         else floatingGroups.push(groupIds);
      });

      // Place Anchored Groups
      anchoredGroups.forEach(({ group, anchors }) => {
         const { depthMap, parentMap } = buildHierarchy(group, anchors);
         let avgX = 0, avgY = 0, count = 0;
         anchors.forEach(anchorId => {
            const pos = allPositions[anchorId];
            if (pos) { avgX += pos.x; avgY += pos.y; count++; }
         });

         let baseAngle = fanBaseAngle; 
         if (count > 0) {
            avgX /= count; avgY /= count;
            baseAngle = Math.atan2(avgY - expansionPosition.y, avgX - expansionPosition.x);
         }

         const maxDepth = Math.max(...Object.values(depthMap));
         for (let d = 0; d <= maxDepth; d++) {
             const nodesAtDepth = group.filter(id => depthMap[id] === d);
             nodesAtDepth.forEach((nodeId, idx) => {
                 let parentPos = { x: count > 0 ? avgX : expansionPosition.x, y: count > 0 ? avgY : expansionPosition.y };
                 const parentId = parentMap[nodeId];
                 if (parentId && nodePositions[parentId]) parentPos = nodePositions[parentId];
                 else if (parentId && allPositions[parentId]) parentPos = allPositions[parentId];

                 const spreadWidth = 0.6; 
                 const offsetAngle = (nodesAtDepth.length > 1) 
                    ? -spreadWidth/2 + (idx * spreadWidth / (nodesAtDepth.length - 1))
                    : 0;
                 const placementAngle = baseAngle + offsetAngle;
                 const dist = 70 + Math.random() * 20;

                 nodePositions[nodeId] = {
                     x: parentPos.x + dist * Math.cos(placementAngle),
                     y: parentPos.y + dist * Math.sin(placementAngle)
                 };
             });
         }
         // Fallback
         group.forEach(nodeId => {
             if (!nodePositions[nodeId]) {
                 nodePositions[nodeId] = { x: expansionPosition.x + 50, y: expansionPosition.y };
             }
         });
      });

      // E2: Place Floating Groups (Smart Spiral)
      const totalFloatingNodes = floatingGroups.reduce((acc, g) => acc + g.length, 0);
      let globalList: string[] = [];
      floatingGroups.forEach(g => globalList.push(...g));
      
      const fanStartAngle = fanBaseAngle - (fanSector / 2);
      
      globalList.forEach((nodeId, index) => {
         // Spiral Calculation to prevent overlap
         const spacing = 60;
         const angleStep = 0.5; // Radians per node in spiral
         const spiralOffset = index * angleStep;
         
         const angle = fanStartAngle + (index % (fanSector / 0.3) * 0.3) + spiralOffset; // Mix fan and spiral
         const radius = 120 + (index * 15); // Growing radius

         nodePositions[nodeId] = {
             x: expansionPosition.x + radius * Math.cos(angle),
             y: expansionPosition.y + radius * Math.sin(angle)
         };
      });

      // Process updates for existing nodes (neighbors etc that get new labels)
      const updatesForExistingNodes: any[] = [];
      
      newNodes.forEach((newNode: any) => {
        const nodeIdStr = newNode.id?.toString();
        const exists = currentNodeIds.has(nodeIdStr);

        if (exists) {
          // Retrieve current state
          let currentNode = graphNodes.get(newNode.id) as any;
          if (!currentNode && nodeIdStr) {
             currentNode = graphNodes.get(nodeIdStr) as any;
          }

          if (currentNode) {
             // Merge properties carefully
             const mergedProperties = {
                ...(currentNode.properties || {}),
                ...(newNode.properties || {})
             };
             
             // Ensure specific fields are carried over
             if (newNode.properties && newNode.properties.funktionar_namn) {
                mergedProperties.funktionar_namn = newNode.properties.funktionar_namn;
             }

             const mergedNode = {
               ...currentNode, // Base on existing
               ...newNode,     // Overwrite with new
               properties: mergedProperties,
               // Keep position
               x: currentNode.x,
               y: currentNode.y
             };
             updatesForExistingNodes.push(mergedNode);
          }
        }
      });

      // Apply styling to updated existing nodes
      const styledUpdates = getAllNodesWithLabels(
          updatesForExistingNodes, 
          orgNumber, 
          graphMetaData, 
          highlightedNodes, 
          new Set(), 
          false, 
          expandedNodes, 
          nodeLabelSettings,
          isDarkMode,
          showCompanyComments
      );

      // Create update payloads
      const existingNodesToUpdate: any[] = [];
      styledUpdates.forEach((node: any) => {
        const nodeIdStr = node.id?.toString();
        const currentNode = graphNodes.get(node.id) as any || (nodeIdStr ? graphNodes.get(nodeIdStr) as any : null);
        
        if (currentNode) {
          const updatedNode = {
            ...currentNode,
            ...node,
            id: currentNode.id,
            x: currentNode.x,
            y: currentNode.y,
            properties: node.properties,
            metadata: {
              ...currentNode.metadata,
              ...(node as any).metadata,
              cumulativeRelationshipTypes: (node as any).metadata?.cumulativeRelationshipTypes,
              isOnBadPath: (node as any).metadata?.isOnBadPath
            }
          };
          existingNodesToUpdate.push(updatedNode);
        }
      });

      // Apply updates to existing nodes immediately
      if (existingNodesToUpdate.length > 0) {
          graphNodes.update(existingNodesToUpdate);
      }

      // Style Nodes
      const styledNewNodes = getAllNodesWithLabels(
        sortedNewNodes,
        orgNumber,
        graphMetaData,
        highlightedNodes,
        new Set(),
        false,
        expandedNodes,
        nodeLabelSettings,
        isDarkMode,
        showCompanyComments
      ).map((node) => {
          const id = String(node.id);
          const pos = nodePositions[id] || { x: expansionPosition.x, y: expansionPosition.y };
          return { ...node, id: id, x: pos.x, y: pos.y };
      });

      // Style Edges
      const newEdgesToAdd: Edge[] = [];
      const styledEdges = getAllEdgesWithLabels(
        newRelationships, graphVizBehaviourOptions, getAccessToken(), [...updatesForExistingNodes, ...styledNewNodes], new Set(), false, edgeLabelSettings
      );
      styledEdges.forEach((edge: any) => {
          if (edge.id == null || !currentEdgeIds.has(String(edge.id))) {
              const baseLength = physicsSettings?.springLength || 160;
              newEdgesToAdd.push({ ...edge, length: baseLength * (0.8 + Math.random() * 0.4) } as any);
          }
      });

      // Update Dataset (Batch)
      // Add new nodes as hidden — they'll be revealed by the slide animation after stabilization
      const hiddenStyledNewNodes = styledNewNodes.map(node => ({
        ...node,
        hidden: true,
      }));
      if (hiddenStyledNewNodes.length > 0) graphNodes.add(hiddenStyledNewNodes as any);
      if (newEdgesToAdd.length > 0) graphEdges.add(newEdgesToAdd);

      // State Updates
      setNewlyAddedNodes(prev => {
          const updated = new Set(prev);
          styledNewNodes.forEach(n => updated.add(String(n.id)));
          return updated;
      });
      setNewlyAddedEdges(prev => {
          const updated = new Set(prev);
          newEdgesToAdd.forEach(e => updated.add(String(e.id)));
          return updated;
      });
      setShowNewAdditions(true);

      // 4. STABILIZATION
      const network = networkRef.current;
      if (network) {
          // Capture viewport (zoom + position) before stabilization so we can restore it after
          viewportStateManager.captureViewport(network);

          setExpansionMessage(i18n.t('graph.expansion.stabilizing'));
          setStabilizationComplete?.(false);
          network.stopSimulation();
          
          // A2: Switch to BarnesHut for faster, more robust untangling
          network.setOptions({
            physics: {
              enabled: true,
              solver: 'barnesHut',
              barnesHut: {
                gravitationalConstant: -8000, 
                springConstant: 0.04,
                springLength: 150,
                damping: 0.5
              },
              stabilization: {
                enabled: true,
                iterations: 500,
                updateInterval: 25,
                onlyDynamicEdges: false
              }
            }
          });
          
          let stabilizationFinished = false;
          let stabilizationTimeout: NodeJS.Timeout | null = null;
          let dragHandler: (() => void) | null = null;
          let completeHandler: (() => void) | null = null;
          
          const finalize = () => {
              if (stabilizationFinished) return;
              stabilizationFinished = true;
              
              if (stabilizationTimeout) clearTimeout(stabilizationTimeout);
              if (dragHandler) network.off('dragStart', dragHandler);
              if (completeHandler) network.off('stabilizationIterationsDone', completeHandler);

              console.log('Stabilization done. Preparing slide animation.');
              
              setStabilizationComplete?.(true);

              // Update positions from network layout
              networkRef.current?.storePositions();
              const currentPositions = networkRef.current?.getPositions() || {};

              // Update styledNewNodes with final positions so they persist correctly
              const finalNewNodes = styledNewNodes.map(node => {
                const idStr = String(node.id);
                const pos = currentPositions[idStr];
                return {
                  ...node,
                  x: pos ? pos.x : node.x,
                  y: pos ? pos.y : node.y
                };
              });

              // Restore High-Quality Solver (before animation starts)
              network.setOptions({ 
                  physics: { 
                      solver: 'forceAtlas2Based',
                      forceAtlas2Based: physicsSettings 
                  }
              });

              // ── Build the slide animation data ──────────────────────────
              // Collect all nodes that need to animate:
              //  - New nodes: from their initial calculated position → final stabilized position
              //  - Existing neighbors: from their pre-expansion position → final stabilized position
              const RELOCATION_THRESHOLD = 20;
              const animatingNodes: AnimatingNode[] = [];

              // 1) New nodes — animate from initial placement to final position
              styledNewNodes.forEach(node => {
                const idStr = String(node.id);
                const finalPos = currentPositions[idStr];
                if (finalPos) {
                  // Initial position is where we placed them (nodePositions)
                  const initialPos = nodePositions[idStr] || { x: expansionPosition.x, y: expansionPosition.y };
                  animatingNodes.push({
                    id: idStr,
                    fromX: initialPos.x,
                    fromY: initialPos.y,
                    toX: finalPos.x,
                    toY: finalPos.y,
                    isNew: true,
                  });
                }
              });

              // 2) Existing neighbors that moved — animate from pre-expansion to final
              nodesToKeepUnfrozen.forEach(nid => {
                if (!currentNodeIds.has(nid)) return; // skip new nodes (handled above)
                const pre = preExpansionPositions[nid];
                const post = currentPositions[nid];
                if (pre && post) {
                  const d = Math.sqrt(
                    Math.pow(post.x - pre.x, 2) + Math.pow(post.y - pre.y, 2)
                  );
                  if (d >= RELOCATION_THRESHOLD) {
                    animatingNodes.push({
                      id: nid,
                      fromX: pre.x,
                      fromY: pre.y,
                      toX: post.x,
                      toY: post.y,
                      isNew: false,
                    });
                  }
                }
              });

              // Un-hide new nodes (they were hidden during stabilization)
              // and set all animated nodes to their FROM positions.
              // The slide animation hook will handle the rest.
              const unhideUpdates = styledNewNodes.map(node => ({
                id: String(node.id),
                hidden: false,
              }));
              if (unhideUpdates.length > 0) graphNodes.update(unhideUpdates as any);

              // Unlock non-animated nodes so they're draggable
              const animatingIds = new Set(animatingNodes.map(n => n.id));
              const nonAnimatedUnlocks = graphNodes.getIds()
                .filter(id => !animatingIds.has(String(id)))
                .map(id => {
                  const strId = String(id);
                  const update: any = { id: strId, fixed: false };
                  if (currentPositions[strId]) {
                    update.x = currentPositions[strId].x;
                    update.y = currentPositions[strId].y;
                  }
                  return update;
                });
              if (nonAnimatedUnlocks.length > 0) graphNodes.update(nonAnimatedUnlocks);

              // Kick off the slide animation
              if (onSlideAnimation && animatingNodes.length > 0) {
                console.log(`✨ ${animatingNodes.length} node(s) will slide into position`);
                onSlideAnimation(animatingNodes);
              } else {
                // No animation needed — just unlock everything at final positions
                const allUpdates = graphNodes.getIds().map(id => {
                  const strId = String(id);
                  const update: any = { id: strId, fixed: false };
                  if (currentPositions[strId]) {
                    update.x = currentPositions[strId].x;
                    update.y = currentPositions[strId].y;
                  }
                  return update;
                });
                graphNodes.update(allUpdates);
              }

              if (onExpansionComplete) onExpansionComplete(finalNewNodes, newEdgesToAdd as Edge[]);
              setGraphDataVersion(prev => prev + 1);
              isExpanding.current = false;
              setIsExpansionLoading(false);
              setExpansionMessage('');

              // Restore viewport AFTER state updates so that React effects
              // (which may call graphNodes.update) run first. Using nested
              // requestAnimationFrame ensures we paint after the browser has
              // processed any vis-network redraws triggered by those effects.
              requestAnimationFrame(() => {
                viewportStateManager.restoreViewport(network);
                // Second frame as a safety net — some vis-network internal
                // redraws are themselves scheduled via rAF.
                requestAnimationFrame(() => {
                  viewportStateManager.restoreViewport(network);
                });
              });
          };

          completeHandler = finalize;
          
          // B1: Interrupt on user interaction
          dragHandler = () => {
             console.log("User interrupted stabilization");
             network.stopSimulation();
             finalize();
          };

          network.on('stabilizationIterationsDone', completeHandler);
          network.on('dragStart', dragHandler);

          network.stabilize(500);

          // B1: Safety Timeout
          stabilizationTimeout = setTimeout(() => {
            console.warn('Stabilization timed out');
            finalize();
          }, 4000);

      } else {
        // Fallback
        isExpanding.current = false;
        setIsExpansionLoading(false);
      }
    } catch (error) {
        console.error('Simplified node expansion failed:', error);
        setIsExpansionLoading(false);
        setExpansionMessage('');
        isExpanding.current = false;
        // Emergency unlock
        try {
          const allNodeIds = graphNodes.getIds();
          graphNodes.update(allNodeIds.map(id => ({ id: String(id), fixed: false })));
        } catch (u) {}
        throw error;
    }
    } catch (outerError) {
      console.error('Error during node expansion (outer):', outerError);
      setIsExpansionLoading(false);
      setExpansionMessage(i18n.t('graph.expansion.failed'));
      isExpanding.current = false;
      throw outerError;
    }
  };

  return { handleExpandNode };
};
